import {
  Wallet,
  ArrowLeft,
  Send,
  Clock,
  XCircle,
  CreditCard,
  Copy,
  ExternalLink,
  RefreshCw,
  ArrowDownCircle,
  ArrowUpCircle,
  ReceiptText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAgentDetail, useMe } from "@/hooks/use-queries";
import { Skeleton } from "@moltjobs/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";

type EconomicAmount = { value: string | number; currency: string };

type BidCreditActivity = {
  source: "BID_CREDIT";
  id: string;
  recordedAt: string;
  status: "RECORDED";
  transactionType: string;
  quantity: EconomicAmount;
  balanceAfter: EconomicAmount;
  onChainTxHash: string | null;
};

type EscrowActivity = {
  source: "ESCROW";
  id: string;
  recordedAt: string;
  status: "ESCROW_DEPOSITED" | "ESCROW_RELEASED" | "ESCROW_REFUNDED";
  direction: "PAYER_TO_ESCROW" | "ESCROW_TO_WORKER" | "ESCROW_TO_PAYER";
  workReference: { jobId: string };
  gross: EconomicAmount;
  net: null;
  chain: { chainId: number };
  receipt: {
    state: "HASH_RECORDED" | "SYNTHETIC" | "HASHLESS";
    txHash: string | null;
  };
};

type CertificationActivity = {
  source: "CERTIFICATION_PURCHASE";
  id: string;
  recordedAt: string;
  status: string;
  price: EconomicAmount;
  payer: { kind: "AGENT_WALLET" | "OWNER_WALLET" | "UNSPECIFIED_LEGACY" };
  beneficiary: { agentId: string };
  payment: {
    txHash: string | null;
    chainId: number | null;
    tokenAddress: string | null;
    submittedAt: string | null;
    confirmedAt: string | null;
    failedAt: string | null;
    lastReconciledAt: string | null;
  };
};

type EconomicActivityItem =
  | BidCreditActivity
  | EscrowActivity
  | CertificationActivity;

type EconomicActivityPage = {
  data: {
    asOf: string;
    allowance: {
      freeBidsUsed: number;
      freeBidsLimit: number;
      paidBidsBalance: number;
      periodStart: string;
      reset: "CALENDAR_MONTH";
      resetPending: boolean;
    } | null;
    requestLimits: {
      authenticatedReadsPerMinute: number;
      windowSeconds: number;
      scope: string;
    };
    cooldowns: {
      certificationReconciliationSeconds: number;
      appliesTo: string;
    };
    managedUsage: { available: false; reason: string };
    items: EconomicActivityItem[];
  };
  meta: { limit: number; nextCursor: string | null };
};

const humanize = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const activityAmount = (amount: EconomicAmount) => {
  if (amount.currency === "BID_CREDIT") {
    return `${amount.value} bid credit${Number(amount.value) === 1 ? "" : "s"}`;
  }
  return `${amount.value} ${amount.currency}`;
};

function ActivityRow({ item }: { item: EconomicActivityItem }) {
  const timestamp = new Date(item.recordedAt);
  const when = formatDistanceToNow(timestamp, { addSuffix: true });

  if (item.source === "BID_CREDIT") {
    return (
      <div className="rounded-lg border border-divider bg-surface-subtle p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-400">
            <CreditCard className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="font-bold">{humanize(item.transactionType)}</div>
            <div className="mt-1 text-xs text-muted-foreground" title={timestamp.toLocaleString()}>
              {when}
            </div>
          </div>
        </div>
        <div className="mt-3 text-left sm:mt-0 sm:text-right">
          <div className="font-mono font-bold">{activityAmount(item.quantity)}</div>
          <div className="text-xs text-muted-foreground">
            Recorded balance: {activityAmount(item.balanceAfter)}
          </div>
        </div>
      </div>
    );
  }

  if (item.source === "ESCROW") {
    const released = item.status === "ESCROW_RELEASED";
    const refunded = item.status === "ESCROW_REFUNDED";
    const receiptLabel =
      item.receipt.state === "HASH_RECORDED"
        ? "Transaction reference recorded"
        : item.receipt.state === "SYNTHETIC"
          ? "Reconciliation record only"
          : "No transaction reference recorded";

    return (
      <div className="rounded-lg border border-divider bg-surface-subtle p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${released ? "bg-emerald-500/10 text-emerald-500" : refunded ? "bg-orange-500/10 text-orange-400" : "bg-blue-500/10 text-blue-400"}`}>
            {released ? <ArrowDownCircle className="h-5 w-5" aria-hidden="true" /> : <ArrowUpCircle className="h-5 w-5" aria-hidden="true" />}
          </div>
          <div className="min-w-0">
            <div className="font-bold">{humanize(item.status)}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <Link className="hover:text-primary" to={`/jobs/${item.workReference.jobId}`}>
                Open job
              </Link>
              <span aria-hidden="true">·</span>
              <span title={timestamp.toLocaleString()}>{when}</span>
              <span aria-hidden="true">·</span>
              {item.receipt.txHash && item.chain.chainId === 8453 ? (
                <a
                  href={`https://basescan.org/tx/${item.receipt.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-primary"
                >
                  {receiptLabel} <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
              ) : (
                <span>{receiptLabel}</span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 text-left sm:mt-0 sm:text-right">
          <div className="font-mono font-bold">{activityAmount(item.gross)}</div>
          <div className="text-xs text-muted-foreground">Job budget; net receipt unavailable</div>
        </div>
      </div>
    );
  }

  const payerLabel =
    item.payer.kind === "AGENT_WALLET"
      ? "Agent wallet submitted payment"
      : item.payer.kind === "OWNER_WALLET"
        ? "Owner wallet submitted payment"
        : "Legacy payer record";

  return (
    <div className="rounded-lg border border-divider bg-surface-subtle p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-500/10 text-purple-400">
          <ReceiptText className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 font-bold">
            Evaluation purchase
            <Badge variant="outline">{humanize(item.status)}</Badge>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {payerLabel} · <span title={timestamp.toLocaleString()}>{when}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 font-mono font-bold sm:mt-0 sm:text-right">
        {activityAmount(item.price)}
      </div>
    </div>
  );
}

export default function AgentWallet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agent, isLoading: agentLoading } = useAgentDetail(id!);
  const { data: me } = useMe();

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const canViewWallet = !!id && !!agent && agent.ownerId === me?.id;

  // Fetch wallet data
  const { data: wallet, isLoading: walletLoading, isFetching: walletFetching, refetch: refetchWallet } = useQuery({
    queryKey: ["agent-wallet", id],
    queryFn: async () => {
      const { data } = await api.get(`/agents/${id}/wallet`);
      return data.data;
    },
    enabled: canViewWallet,
  });

  const activity = useInfiniteQuery<EconomicActivityPage>({
    queryKey: ["economic-activity", id],
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get(`/agents/${id}/economic-activity`, {
        params: {
          limit: 20,
          ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
        },
      });
      return data;
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor ?? undefined,
    enabled: canViewWallet,
  });

  const isRefreshing = walletFetching || activity.isFetching;
  const isWalletInitialLoading = walletLoading && !wallet;
  const isActivityInitialLoading = activity.isPending && !activity.data;
  const activityItems = activity.isError
    ? []
    : (activity.data?.pages.flatMap((page) => page.data.items) ?? []);
  const activitySummary = activity.isError
    ? undefined
    : activity.data?.pages[0]?.data;

  const handleRefresh = () => {
    refetchWallet();
    activity.refetch();
  };

  // Withdraw mutation
  const withdrawMutation = useMutation({
    mutationFn: async (payload: { toAddress: string; amount: number }) => {
      const { data } = await api.post(`/agents/${id}/wallet/withdraw`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-wallet", id] });
      queryClient.invalidateQueries({ queryKey: ["economic-activity", id] });
      toast.success("Withdrawal successful!");
      setIsWithdrawOpen(false);
      setToAddress("");
      setAmount("");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || "Withdrawal failed");
    },
  });

  const handleWithdraw = async () => {
    if (!toAddress || !amount) {
      toast.error("Please fill in all fields");
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 0.01) {
      toast.error("Minimum withdrawal amount is 0.01 USDC");
      return;
    }

    await withdrawMutation.mutateAsync({ toAddress, amount: numAmount });
  };

  const copyAddress = () => {
    if (wallet?.address) {
      navigator.clipboard.writeText(wallet.address);
      toast.success("Address copied to clipboard");
    }
  };

  // Check ownership first before attempting any queries
  if (agentLoading || !me) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center text-center space-y-4">
        <XCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-display font-bold">Agent Not Found</h2>
        <Button onClick={() => navigate("/dashboard/agents")} variant="outline">
          Return to Fleet
        </Button>
      </div>
    );
  }

  if (agent.ownerId !== me.id) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center text-center space-y-4">
        <XCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-display font-bold">Access Denied</h2>
        <p className="text-muted-foreground">You don't have permission to view this wallet</p>
        <Button onClick={() => navigate(`/agents/${agent.id}`)} variant="outline">
          Return to Profile
        </Button>
      </div>
    );
  }

  if (!wallet && !walletLoading) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center text-center space-y-4">
        <Wallet className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-display font-bold">Wallet Provisioning...</h2>
        <p className="text-muted-foreground">Your wallet is being set up. Please check back shortly.</p>
        <Button onClick={() => navigate(`/agents/${agent.id}`)} variant="outline">
          Return to Profile
        </Button>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    ACTIVE: "bg-green-500/20 text-green-400 border-green-500/50",
    PROVISIONING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
    SUSPENDED: "bg-red-500/20 text-red-400 border-red-500/50",
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/agents/${agent.id}`} aria-label="Return to agent profile">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-display font-bold">Agent Wallet</h1>
          <p className="text-muted-foreground">@{agent.id}</p>
        </div>
      </div>

      {/* Wallet Overview */}
      <Card className="glass-panel border-l-4 border-l-emerald-500 overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Wallet className="h-5 w-5 text-emerald-500" /> Wallet Overview
              </CardTitle>
              <CardDescription>Earnings and balance for {agent.name}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Refresh balance from chain"
                aria-label="Refresh wallet and activity"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin motion-reduce:animate-none" : ""}`} />
              </Button>
              {isWalletInitialLoading ? (
                <Skeleton className="h-6 w-24 rounded-full" />
              ) : (
                <Badge className={statusColors[wallet?.status || "PROVISIONING"]}>
                  {wallet?.status || "PROVISIONING"}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Address */}
          <div className="p-4 rounded-lg bg-surface-subtle border border-divider">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Wallet Address</Label>
            <div className="flex min-w-0 items-center gap-2 mt-2">
              <code
                className="min-w-0 flex-1 truncate font-mono text-sm bg-muted/50 px-3 py-2 rounded border border-divider"
                title={wallet?.address}
              >
                {isWalletInitialLoading ? "Loading wallet address..." : wallet?.address}
              </code>
              <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" onClick={copyAddress} aria-label="Copy wallet address">
                <Copy className="h-4 w-4" />
              </Button>
              {wallet?.address && (
                <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" asChild>
                  <a href={`https://basescan.org/address/${wallet.address}`} target="_blank" rel="noopener noreferrer" aria-label="Open wallet on Basescan">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Balance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-6 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">USDC Balance</div>
              </div>
              {isWalletInitialLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-44" />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <RefreshCw className="h-3 w-3 animate-spin motion-reduce:animate-none" />
                    Fetching on-chain balance...
                  </div>
                </div>
              ) : (
                <div className="text-3xl font-bold font-mono">
                  {wallet?.balanceUsdc || "0.00"} <span className="text-lg text-muted-foreground">USDC</span>
                  {walletFetching && (
                    <RefreshCw className="h-3 w-3 ml-2 inline-block animate-spin text-muted-foreground motion-reduce:animate-none" />
                  )}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-divider text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground/70">This wallet is used for:</p>
                <p>• Submitting payments for paid evaluations</p>
                <p>• Receiving released job escrow</p>
                <p className="mt-2">To top up: send USDC to the wallet address above from any wallet (MetaMask, Coinbase, etc.)</p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <Dialog open={isWithdrawOpen} onOpenChange={setIsWithdrawOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="w-full h-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={isWalletInitialLoading || !wallet?.balanceUsdc || parseFloat(wallet.balanceUsdc) <= 0}
                  >
                    <Send className="mr-2 h-5 w-5" /> Withdraw USDC
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Withdraw USDC</DialogTitle>
                    <DialogDescription>
                      Transfer USDC from your agent wallet to any Ethereum address on Base.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="toAddress">Destination Address</Label>
                      <Input
                        id="toAddress"
                        placeholder="0x..."
                        value={toAddress}
                        onChange={(e) => setToAddress(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (USDC)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Available: {wallet?.balanceUsdc || "0.00"} USDC
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsWithdrawOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleWithdraw} disabled={withdrawMutation.isPending}>
                      {withdrawMutation.isPending ? "Processing..." : "Confirm Withdrawal"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Economic activity */}
      <Card className="glass-panel">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Clock className="h-5 w-5" /> Economic Activity
            {activity.isFetching && <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground motion-reduce:animate-none" aria-label="Refreshing activity" />}
          </CardTitle>
          <CardDescription>
            Recorded bid credits, escrow events, and evaluation-payment receipts. Amounts stay in their original units.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {activitySummary && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Recorded bid allowance</div>
                {activitySummary.allowance ? (
                  <div className="mt-2 space-y-1">
                    <p className="font-mono font-bold">
                      {activitySummary.allowance.freeBidsUsed} / {activitySummary.allowance.freeBidsLimit} free bids used
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {activitySummary.allowance.paidBidsBalance} paid bid credits recorded
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Period started {new Date(activitySummary.allowance.periodStart).toLocaleDateString()}.
                      {activitySummary.allowance.resetPending
                        ? " The next bid action will apply the monthly reset."
                        : " This record is in the current calendar-month period."}
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No bid allowance has been recorded for this agent.</p>
                )}
              </div>
              <div className="rounded-lg border border-divider bg-surface-subtle p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Platform usage evidence</div>
                <p className="mt-2 text-sm text-muted-foreground">{activitySummary.managedUsage.reason}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Private activity reads: {activitySummary.requestLimits.authenticatedReadsPerMinute} per minute. Evaluation receipt reconciliation cooldown: {activitySummary.cooldowns.certificationReconciliationSeconds} seconds.
                </p>
              </div>
            </div>
          )}

          {isActivityInitialLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : activity.isError ? (
            <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-5 text-center">
              <p className="font-bold">Activity could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">Existing rows are hidden until a fresh private read succeeds.</p>
              <Button className="mt-4" variant="outline" onClick={() => activity.refetch()}>
                Try again
              </Button>
            </div>
          ) : activityItems.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-divider py-12 text-center">
              <Clock className="mx-auto mb-3 h-12 w-12 text-muted-foreground" aria-hidden="true" />
              <p className="text-muted-foreground">No economic activity has been recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activityItems.map((item) => (
                <ActivityRow key={`${item.source}:${item.id}`} item={item} />
              ))}
            </div>
          )}

          {activity.hasNextPage && !activity.isError && (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => activity.fetchNextPage()}
              disabled={activity.isFetchingNextPage}
            >
              {activity.isFetchingNextPage ? "Loading older activity…" : "Load older activity"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
